import * as Axios from 'axios';
import autobind from 'autobind-decorator';
import { injectable, inject } from 'inversify';
import * as querystring from 'querystring';

import { ConfigurationService } from './configuration.service';

@injectable()
export class IdentityService {
	private static storageKey: string = 'zulu-session';
	public userId: number | null;
	public username: string;
	public accessToken: string;
	public isAuthenticated: boolean;
	public isAdmin: boolean;
	public roles: string[];
	public permissions: string[];
	public displayName: string;
	public defaultLanguage: string;
	public defaultDestiny: number;
	public defaultMarket: number;
	public defaultBrand: number;
	public refreshToken: string;
	private createdDate: Date;

	public canGenerateDocuments: boolean;

	@inject(ConfigurationService)
	protected configurationService: ConfigurationService;

	constructor() {
		this.isAuthenticated = false;

		const token = localStorage.getItem(IdentityService.storageKey);
		if (token) {
			const decoding = atob(token);
			const json = JSON.parse(decoding);
			this.username = json.username;
			this.accessToken = json.accessToken;
			this.isAuthenticated = true;
			this.roles = json.role;
			this.userId = json.userId;
			this.displayName = json.displayName;
			this.defaultLanguage = json.defaultLanguage;
			this.defaultDestiny = json.defaultDestiny;
			this.defaultMarket = json.defaultMarket;
			this.defaultBrand = json.defaultBrand;
			this.refreshToken = json.refreshToken;
			this.isAdmin = json.isAdmin === true || json.isAdmin === "true";
			this.permissions = json.permission;
			this.setDocumentGeneration();
			this.createdDate = new Date(json.createdDate);
		}
	}

	private setDocumentGeneration() {
		this.canGenerateDocuments = this.roles.some(r => r === "Superadmin")
			|| this.permissions.some((r) => r === "DocumentGeneration");
	}

	@autobind
	public isAuthorized(permission: string | string[]): boolean {
		return this.isAuthenticated && (
			this.roles.some(r => r === "Superadmin") ||
			(Array.isArray(permission) ? this.isAuthorizedList(permission) : this.permissions.some(m => m === permission))
		);
	}

	@autobind
	public logOff() {
		this.clearContext();
	}

	@autobind
	public refreshTokenAction() {
		return new Promise<boolean>((resolve, reject) => {

			this.configurationService.current()
				.then((appConfig) => {
					const http = Axios.default.create({
						timeout: 30000,
						baseURL: appConfig.serviceUrl
					});

					http.post('/token', querystring.stringify({
						grant_type: 'refresh_token',
						refresh_token: this.refreshToken
					}))
						.then((response) => {
							this.clearContext();
							this.processResponse(response);
							resolve(true);
						})
						.catch((error) => {
							this.logOff();
						});
				})
				.catch(reject);
		});
	}

	@autobind
	public authenticate(username: string, password: string): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			this.clearContext();

			this.configurationService.current()
				.then((appConfig) => {
					const http = Axios.default.create({
						timeout: 30000,
						baseURL: appConfig.serviceUrl,
					});

					http.post('/token', querystring.stringify({
						grant_type: 'password',
						username: username,
						password: password,
					}))
						.then((response) => {
							this.processResponse(response);
							resolve(true);
						})
						.catch((error) => {
							if (error.response && error.response.data && error.response.data.error && error.response.data.error == 'invalid_grant') {
								reject({ message: 'Access is denied' });
							} else {
								reject(error);
							}
						});
				})
				.catch(reject);
		});
	}

	private isAuthorizedList(permissions: string[]) {
		for (const permission of permissions) {
			if (this.permissions.some(p => p === permission)) {
				return true;
			}
		}
		return false;
	}

	private clearContext() {
		localStorage.removeItem(IdentityService.storageKey);
		this.username = '';
		this.accessToken = '';
		this.isAuthenticated = false;
		this.roles = [];
		this.userId = -1;
		this.displayName = '';
		this.defaultLanguage = null;
		this.defaultDestiny = null;
		this.defaultMarket = null;
		this.defaultBrand = null;
		this.refreshToken = '';
		this.isAdmin = false;
		this.permissions = [];
		this.setDocumentGeneration();
	}

	private processResponse(response: Axios.AxiosResponse) {
		this.username = response.data['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
		this.accessToken = response.data.access_token;
		this.isAuthenticated = true;
		this.roles = response.data['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?
			response.data['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'].split(',')
				.map(m => m.trim()) : [];
		this.defaultLanguage = response.data['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/locality'];
		this.displayName = response.data['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'];
		this.userId = Number.parseInt(response.data['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']);

		const destiny = response.data['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/country'];
		this.defaultDestiny = destiny ? Number.parseInt(destiny) : undefined;
		const market = response.data.Market;
		this.defaultMarket = market ? Number.parseInt(market) : undefined;
		const brand = response.data.Brand;
		this.defaultBrand = brand ? Number.parseInt(brand) : brand;
		this.refreshToken = response.data.refresh_token;
		this.isAdmin = response.data.IsAdmin === true || response.data.IsAdmin === "true";
		this.permissions = response.data.Permission
			? response.data.Permission.split(',').map(m => m.trim())
			: [];
		this.setDocumentGeneration();
		this.createdDate = new Date();

		const json = {
			username: this.username,
			accessToken: this.accessToken,
			role: this.roles,
			userId: this.userId,
			displayName: this.displayName,
			defaultLanguage: this.defaultLanguage,
			defaultDestiny: this.defaultDestiny,
			defaultMarket: this.defaultMarket,
			defaultBrand: this.defaultBrand,
			refreshToken: this.refreshToken,
			isAdmin: this.isAdmin,
			permission: this.permissions,
			createdDate: this.createdDate,
		};
		const encoding = btoa(JSON.stringify(json));
		localStorage.setItem(IdentityService.storageKey, encoding);
	}
}



// WEBPACK FOOTER //
// ./src/core/services/identity.service.ts